import { environment } from 'environments/environment';
import { ensureHttpsUrl } from 'app/auth/helpers/ssl-auth.util';

/** URLs hébergées Agrosilo — jamais de localhost pour le setup LC. */
export const HOSTED_ERP_URLS = {
  hub: 'https://front-hub.agrosilo-solutions.com',
  vente: 'https://front-vente.agrosilo-solutions.com',
  achat: 'https://front-achat.agrosilo-solutions.com',
  comptabilite: 'https://front-comptabilite.agrosilo-solutions.com',
  parametre: 'https://front-parametrage.agrosilo-solutions.com',
  parametre_data: 'https://front-paramatrage-donne.agrosilo-solutions.com',
  logiciel_caisse: 'https://front-logiciel-caisse.agrosilo-solutions.com',
} as const;

const LOCAL_PORT_TO_HOSTED: Record<string, string> = {
  '4190': HOSTED_ERP_URLS.hub,
  '4300': HOSTED_ERP_URLS.parametre,
  '4200': HOSTED_ERP_URLS.vente,
  '4205': HOSTED_ERP_URLS.vente,
  '4201': HOSTED_ERP_URLS.achat,
  '4202': HOSTED_ERP_URLS.comptabilite,
  '4203': HOSTED_ERP_URLS.parametre_data,
  '4500': HOSTED_ERP_URLS.logiciel_caisse,
};

/** Base URL du front-hub (auth / accès logiciel caisse centralisés) — HTTPS forcé hors localhost. */
export function getLogicielCaisseHubBaseUrl(): string {
  const fromEnv = String((environment as { hubUrl?: string }).hubUrl || '').trim();
  if (fromEnv && !/localhost|127\.0\.0\.1/i.test(fromEnv)) {
    return ensureHttpsUrl(fromEnv.replace(/\/$/, ''));
  }
  return HOSTED_ERP_URLS.hub;
}

/**
 * Remplace http://localhost:PORT/... par l’équivalent hébergé.
 * Évite returnUrl / redirections vers localhost après setup LC.
 */
export function rewriteLocalhostToHostedUrl(url: string | null | undefined): string {
  const raw = String(url || '').trim();
  if (!raw) {
    return raw;
  }
  try {
    const absolute = /^https?:\/\//i.test(raw)
      ? raw
      : `${window.location.origin}${raw.startsWith('/') ? raw : `/${raw}`}`;
    const u = new URL(absolute);
    if (u.hostname !== 'localhost' && u.hostname !== '127.0.0.1') {
      return /^https?:\/\//i.test(raw) ? raw : `${u.pathname}${u.search}${u.hash}`;
    }
    const hostedBase = (LOCAL_PORT_TO_HOSTED[u.port] || HOSTED_ERP_URLS.hub).replace(/\/$/, '');
    return `${hostedBase}${u.pathname}${u.search}${u.hash}`;
  } catch {
    return raw;
  }
}

/**
 * Redirige vers les écrans d'accès logiciel caisse sur front-hub hébergé
 * (plus de setup local / localhost sur front-parametre).
 * Embarque `auth=` (ERP + LC) pour que le hub retrouve la session.
 */
export function redirectToHubAccesLogicielCaisse(
  path: string,
  returnUrl?: string | null
): void {
  const hub = ensureHttpsUrl(getLogicielCaisseHubBaseUrl());
  const clean = String(path || '/tiers/acces-logiciel-caisse').trim();
  const withSlash = clean.startsWith('/') ? clean : `/${clean}`;
  const safeReturn = returnUrl ? ensureHttpsUrl(rewriteLocalhostToHostedUrl(returnUrl)) : '';

  const u = new URL(`${hub}${withSlash}`);
  if (safeReturn && String(safeReturn).trim()) {
    u.searchParams.set('returnUrl', String(safeReturn).trim());
  }

  try {
    const payload = btoa(
      JSON.stringify({
        token: localStorage.getItem('token') || localStorage.getItem('erp_token'),
        users: localStorage.getItem('users'),
        currentUser: localStorage.getItem('currentUser'),
        logiciel_caisse_token: localStorage.getItem('logiciel_caisse_token'),
        logiciel_caisse_session: localStorage.getItem('logiciel_caisse_session'),
      })
    );
    u.searchParams.set('auth', payload);
  } catch {
    /* ignore */
  }

  // Ne jamais envoyer les tokens via HTTP distant.
  if (u.protocol === 'http:' && !/localhost|127\.0\.0\.1/i.test(u.hostname)) {
    u.protocol = 'https:';
  }

  window.location.href = u.toString();
}
